/**
 * Base URL for I-3 assets in public/i3/.
 * Use full origin + /i3 so assets always load from the same host (avoids 404
 * when the app is opened at / and the dev server serves public at root).
 */
function getBase(): string {
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/i3`;
  }
  return '/i3';
}

const BASE = getBase();

/** Resolve asset path with correct encoding for spaces (matches filesystem) */
const photoPath = (file: string) => `${BASE}/${encodeURIComponent(file)}`;

export const i3Assets = {
  /** Base path for /i3/ assets (no trailing slash) */
  base: BASE,
  /** Resolve an asset path (e.g. "Troy Solo.png" -> "http://localhost:3010/i3/Troy%20Solo.png") */
  path: (file: string) => `${BASE}/${encodeURIComponent(file)}`,
  /** Logo filenames used by theme (no encoding; filenames have no spaces) */
  logos: {
    earth: `${BASE}/i3-logo-earth.svg`,
    teal: `${BASE}/i3-logo-teal-true-vector.svg`,
    gold: `${BASE}/i3-logo-gold.svg`,
    /** White traced — for intro/video overlay only (reads on dark video) */
    whiteTraced: `${BASE}/i3-logo-white-traced.svg`,
  },
  /** Photo assets — exact filenames from public/i3/ (match filesystem; no spaces = no 404) */
  photos: {
    thisIsUs: photoPath('ThisisUs.png'),
    kironDani: photoPath('Kiron_Dani.png'),
    troySolo: photoPath('TroySolo.png'),
    sharreaSolo: photoPath('SharreaSolo.png'),
    himHer: photoPath('Him-Her.png'),
    /** No space in filename — avoids 404 on some hosts; copy of Funpic 1.png */
    funPic1: photoPath('Funpic1.png'),
    contactHero: photoPath('contact-hero.png'),
  },
  /** Clover payment link — use with existing button styles (no custom Clover styling) */
  cloverPaymentUrl: 'https://link.clover.com/urlshortener/bwjzDX',
} as const;

/**
 * Normalize CMS media paths under `/i3/` so filenames with spaces and parentheses resolve reliably.
 * Respects CRA `PUBLIC_URL` when set. External `http(s)` URLs are returned unchanged.
 */
export function resolvePublicMediaSrc(src: string): string {
  if (!src || typeof src !== 'string') return '';
  const t = src.trim();
  if (/^https?:\/\//i.test(t)) return t;

  const publicBase =
    typeof process !== 'undefined' && process.env.PUBLIC_URL !== undefined
      ? String(process.env.PUBLIC_URL).replace(/\/$/, '')
      : '';

  const m = t.match(/^\/?i3\/(.+)$/i);
  if (m) {
    let name = m[1];
    try {
      name = decodeURIComponent(name);
    } catch {
      /* keep segment as in CMS */
    }
    return `${publicBase}/i3/${encodeURIComponent(name)}`;
  }

  if (t.startsWith('/')) return `${publicBase}${t}`;
  return `${publicBase}/${t}`;
}
